import * as React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { Box, Button, Chip, Divider, Grid2, IconButton, Paper, Stack, styled, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, useTheme, } from '@mui/material';
import { IoMdClose } from 'react-icons/io';
import { useParams } from 'react-router-dom';
import PaginationComponent from 'components/ui-components/PaginationComponent';
import TableDataGrid from 'components/grid/TableDataGrid';
import moment from 'moment';
import ExportToExcel from "../ExportToExcel";
import ExportToPDF from "../ExportToPDF";
import { useGetAllClearanceQuery } from "store/slices/apis/clearancePackageSlice";


const StyledTableHead = styled(TableCell)(({ theme }) => ({
    fontWeight: "400", fontSize: '12px', color: '#37352F', padding: '10px 10px 10px 15px', lineHeight: '100%'
}));

const StyledTableRow = styled(TableCell)(({ theme }) => ({
    fontWeight: "400", fontSize: '12px', color: '#000000', padding: '12px 12px 12px 15px', lineHeight: '100%',
}));

export default function ClearancePopup({ title, value, status, onClose, onOpen }) {

    const theme = useTheme();
    const { projectId } = useParams();
    const [pageNumber, setPageNumber] = React.useState(1);

    let searchparams = {
        projectId: projectId,
        page: pageNumber,
        pageSize: 10,
        sortBy: 'createddate',
        sortOrder: 'desc',
        approved_status: 'Approved',
    }

    if (status == 'Total Clearance') {
        searchparams = {
            ...searchparams
        }
    }
    else if (status == 'Open Clearance') {
        searchparams = {
            ...searchparams,
            status: 'Open',
        }
    }
    else if (status == 'Due this month Clearance') {
        searchparams = {
            ...searchparams,
            dueThisMonth: true,
        }
    }
    else if (status == 'Due next month Clearance') {
        searchparams = {
            ...searchparams,
            dueNextMonth: true,
        }
    }
    else if (status == 'Delaydays') {
        searchparams = {
            ...searchparams,
            delayDaysMin: value.delayDaysMin,
            delayDaysMax: value.delayDaysMax,
            status: 'Open',
        }
    }
    else if (status == 'ApprovingAuthority') {
        searchparams = {
            ...searchparams,
            approving_authority_agency: value,
            status: 'Open',
        }
    }
    else {
        searchparams = {
            ...searchparams,
        }
    }

    const { data: openCloseData = {}, error: openCloseError, isLoading: isOpenCloseLoading } = useGetAllClearanceQuery(searchparams);

    const columns = [
        {
            name: "Clearance ID",
            field: "clearance_number",
            renderer(params) {
                return (
                    <Stack direction="row" spacing={1}>
                        {params?.clearance_number && (
                            <Chip
                                label={params?.clearance_number}
                                size="small"
                                sx={{
                                    fontSize: "12px",
                                    "& .MuiChip-label": {
                                        paddingLeft: "8px",
                                        paddingRight: "8px",
                                        fontWeight: 600,
                                    },
                                }}
                            />
                        )}
                    </Stack>
                );
            },
            renderExport(row) {
                return row?.clearance_number || "";
            }
        },
        {
            name: "Workflow Status",
            field: "approved_status",
            renderer(params) {
                return (
                    <>
                        {params?.approved_status === "Pending_for_Approval"
                            ? "Pending for approval"
                            : params?.approved_status}
                    </>
                );
            },
            renderExport(row) {
                return row?.approved_status === "Pending_for_Approval"
                    ? "Pending for approval"
                    : row?.approved_status || "";
            }
        },
        {
            name: "Status",
            field: "status",
            renderExport(row) {
                return row?.status || "";
            }
        },
        {
            name: "Created Date",
            field: "createddate",
            renderer(params) {
                return (
                    <>
                        {params?.createddate
                            ? moment(params.createddate).format("DD/MM/YY")
                            : "N.A."}
                    </>
                );
            },
            renderExport(row) {
                return row?.createddate
                    ? moment(row.createddate).format("DD/MM/YY")
                    : "N.A.";
            }
        },
        {
            name: "Project",
            field: "project",
            renderExport(row) {
                return row?.project || "";
            }
        },
        {
            name: "Approval Compliance Description",
            field: "approval_compliance_description",
            renderExport(row) {
                return row?.approval_compliance_description || "";
            }
        },
        {
            name: "Compliance Owner",
            field: "compliance_owner",
            renderExport(row) {
                return row?.compliance_owner || "";
            }
        },
        {
            name: "Content Owner",
            field: "content_owner",
            renderExport(row) {
                return row?.content_owner || "";
            }
        },
        {
            name: "Approving Authority Agency",
            field: "approving_authority_agency",
            renderExport(row) {
                return row?.approving_authority_agency || "";
            }
        },
        {
            name: "TAT",
            field: "tat",
            renderExport(row) {
                return row?.tat || 0;
            }
        },
        {
            name: "Plan Date",
            field: "plan_date",
            renderer(params) {
                return (
                    <>
                        {params?.plan_date
                            ? moment(params.plan_date).format("DD/MM/YY")
                            : "N.A."}
                    </>
                );
            },
            renderExport(row) {
                return row?.plan_date
                    ? moment(row.plan_date).format("DD/MM/YY")
                    : "N.A.";
            }
        },
        {
            name: "Actual Expected Date",
            field: "actual_expected_date",
            renderer(params) {
                return (
                    <>
                        {params?.actual_expected_date
                            ? moment(params.actual_expected_date).format("DD/MM/YY")
                            : "N.A."}
                    </>
                );
            },
            renderExport(row) {
                return row?.actual_expected_date
                    ? moment(row.actual_expected_date).format("DD/MM/YY")
                    : "N.A.";
            }
        },
        {
            name: "Revised Plan Date",
            field: "revised_plan_date",
            renderer(params) {
                return (
                    <>
                        {params?.revised_plan_date
                            ? moment(params.revised_plan_date).format("DD/MM/YY")
                            : "N.A."}
                    </>
                );
            },
            renderExport(row) {
                return row?.revised_plan_date
                    ? moment(row.revised_plan_date).format("DD/MM/YY")
                    : "N.A.";
            }
        },
        {
            name: "Escalation Required",
            field: "escalation_required",
            renderer(params) {
                return <>{params?.escalation_required === true ? "Yes" : "No"}</>;
            },
            renderExport(row) {
                return row?.escalation_required === true ? "Yes" : "No";
            }
        },
    ];

    return (
        <React.Fragment>
            <Dialog
                fullWidth={true}
                maxWidth={'lg'}
                open={onOpen}
                onClose={onClose}
                disableAutoFocus
                disableEnforceFocus
                disableRestoreFocus
                sx={{
                    '& .MuiDialog-paper': {
                        overflowY: 'inherit'
                    }
                }}
            >
                <DialogTitle sx={{ fontSize: '20px', color: '#696969' }}>{title}</DialogTitle>
                <IconButton
                    aria-label="close"
                    onClick={onClose}
                    sx={(theme) => ({
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        color: theme.palette.grey[500],
                    })}
                >
                    <IoMdClose style={{ fontSize: '20px' }} />
                </IconButton>
                <Divider sx={{ w: 1 }} />
                <DialogContent>
                    <Box sx={{ width: '100%' }}>
                        <Grid2 container spacing={2}>
                            <Grid2 container spacing={2} size={12}>
                                <Grid2 xs={12} sm={6}>
                                    <ExportToExcel data={openCloseData?.data} columns={columns} fileName="clearance_export" />
                                </Grid2>
                                <Grid2 xs={12} sm={6}>
                                    <ExportToPDF data={openCloseData?.data} columns={columns} fileName="clearance_export" />
                                </Grid2>
                            </Grid2>
                            <Grid2 size={12}>
                                <TableDataGrid columns={columns} data={openCloseData?.data} isLoading={isOpenCloseLoading} />

                                <Box sx={{ display: "flex", justifyContent: "end" }}>
                                    <PaginationComponent
                                        pageNumber={openCloseData?.pagination?.currentPage || 1}
                                        totalPages={openCloseData?.pagination?.totalPages || 1}
                                        onPageChange={setPageNumber}
                                    />
                                </Box>

                            </Grid2>
                        </Grid2>
                    </Box>
                </DialogContent>
            </Dialog>
        </React.Fragment >
    );
}